import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

import { IconAvatar } from "./PageWidgets";
import notificationIcon from "../../assets/images/user-dashboard/notification.svg";
import checkIcon from "../../assets/images/user-dashboard/Open Mail Icon Verified.svg";
import readIcon from "../../assets/images/user-dashboard/Open Mail Icon.svg";
import "./NotificationDropdown.scss";

const NOTIFICATION_ICONS = {
  system: { icon: notificationIcon, tone: "grey" },
};

const NotificationDropdown = ({
  notifications = [],
  unreadCount = 0,
  onMarkAsRead = () => {},
  onViewAll = () => {},
  direction = "down",
  onOpen = () => {},
  viewAllPath = "/notifications",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Refresh notifications when dropdown opens
  useEffect(() => {
    if (isOpen && onOpen) {
      onOpen();
    }
  }, [isOpen, onOpen]);

  const handleMarkAsRead = (e, notificationId) => {
    e.stopPropagation();
    if (onMarkAsRead) {
      onMarkAsRead(notificationId);
    }
  };

  const handleMarkAllAsRead = (e) => {
    e.stopPropagation();
    if (onMarkAsRead) {
      onMarkAsRead("all");
    }
  };

  const handleNotificationClick = (notification) => {
    const notificationUrl = notification.url || notification.redirect_url || null;
    if (!notificationUrl) return;

    if (!notification.read && onMarkAsRead) {
      onMarkAsRead(notification.id);
    }

    setIsOpen(false);

    if (/^https?:\/\//i.test(notificationUrl)) {
      window.location.href = notificationUrl;
      return;
    }

    navigate(notificationUrl);
  };

  const displayNotifications = notifications.slice(0, 5);

  return (
    <div className="notification-dropdown" ref={dropdownRef}>
      <button
        className="notification-dropdown__trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Notifications"
      >
        <img
          src={notificationIcon}
          alt="Notifications"
          className="notification-dropdown__icon"
        />
        {unreadCount > 0 && (
          <span className="notification-dropdown__badge">
            {unreadCount > 5 ? "5+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          className={`notification-dropdown__menu notification-dropdown__menu--${direction}`}
        >
          <div className="notification-dropdown__header">
            <h3 className="notification-dropdown__title">Notifications</h3>
            {unreadCount > 0 && (
              <button
                className="notification-dropdown__mark-all"
                onClick={handleMarkAllAsRead}
              >
                Mark all as read
              </button>
            )}
          </div>

          <div className="notification-dropdown__list">
            {displayNotifications.length > 0 ? (
              displayNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`notification-dropdown__item ${!notification.read ? "notification-dropdown__item--unread" : ""}`}
                  onClick={() => handleNotificationClick(notification)}
                  role={notification.url || notification.redirect_url ? "button" : undefined}
                  tabIndex={notification.url || notification.redirect_url ? 0 : undefined}
                  onKeyDown={(event) => {
                    if ((notification.url || notification.redirect_url) && (event.key === "Enter" || event.key === " ")) {
                      event.preventDefault();
                      handleNotificationClick(notification);
                    }
                  }}
                >
                  <div className="notification-dropdown__item-content">
                    <div className="notification-dropdown__item-icon">
                      <IconAvatar
                        icon={
                          NOTIFICATION_ICONS[notification.notification_type]
                            ?.icon || NOTIFICATION_ICONS.system.icon
                        }
                        tone={
                          NOTIFICATION_ICONS[notification.notification_type]
                            ?.tone || NOTIFICATION_ICONS.system.tone
                        }
                        size={40}
                      />
                    </div>
                    <div className="notification-dropdown__item-text">
                      <h4 className="notification-dropdown__item-title">
                        {notification.title}
                      </h4>
                      <p className="notification-dropdown__item-message">
                        {notification.message}
                      </p>
                      <span className="notification-dropdown__item-time">
                        {notification.timeAgo}
                      </span>
                    </div>
                  </div>
                  {!notification.read && (
                    <button
                      className="notification-dropdown__mark-read"
                      onClick={(e) => handleMarkAsRead(e, notification.id)}
                      aria-label="Mark as read"
                    >
                      <img src={checkIcon} alt="Mark as read" />
                    </button>
                  )}
                  {notification.read && (
                    <img
                      src={readIcon}
                      alt="Read"
                      className="notification-dropdown__read-icon"
                    />
                  )}
                </div>
              ))
            ) : (
              <div className="notification-dropdown__empty">
                <p>No notifications yet</p>
              </div>
            )}
          </div>

          <div className="notification-dropdown__footer">
            <Link
              to={viewAllPath}
              className="notification-dropdown__view-all"
              onClick={() => setIsOpen(false)}
            >
              View all notifications
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

NotificationDropdown.propTypes = {
  notifications: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.string.isRequired,
      message: PropTypes.string.isRequired,
      type: PropTypes.string,
      read: PropTypes.bool,
      timeAgo: PropTypes.string,
      url: PropTypes.string,
      redirect_url: PropTypes.string,
    })
  ),
  unreadCount: PropTypes.number,
  onMarkAsRead: PropTypes.func,
  onViewAll: PropTypes.func,
  direction: PropTypes.oneOf(["up", "down"]),
  onOpen: PropTypes.func,
  viewAllPath: PropTypes.string,
};

export default NotificationDropdown;
